import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore';
import { normalizeCommentReactions } from './commentReactions';
import {
  COMMENT_REACTION_FEED_LIMIT,
  commentAuthorIdFromData,
  commentPreviewFromFields,
  sortCommentReactionEventsNewestFirst,
  type CommentReactionEvent,
} from './commentReactionEvents';

const LIVE_COMMENT_SCAN_LIMIT = 250;
const LIVE_PDF_SCAN_LIMIT = 80;

export function reactionEventKey(event: Pick<CommentReactionEvent, 'commentId' | 'emoji' | 'actorId'>): string {
  return `${event.commentId}|${event.emoji}|${event.actorId}`;
}

export function mergeCommentReactionFeeds(
  logEvents: CommentReactionEvent[],
  liveEvents: CommentReactionEvent[],
): CommentReactionEvent[] {
  const seen = new Set(logEvents.map(reactionEventKey));
  const extras = liveEvents.filter((event) => !seen.has(reactionEventKey(event)));
  return sortCommentReactionEventsNewestFirst([...logEvents, ...extras]).slice(0, COMMENT_REACTION_FEED_LIMIT);
}

function dateFromUnknown(raw: unknown): Date {
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  const ts = raw as { toDate?: () => Date } | undefined;
  const fromTs = ts?.toDate?.();
  if (fromTs instanceof Date && !Number.isNaN(fromTs.getTime())) return fromTs;
  return new Date(0);
}

function eventsFromReactionsMap(args: {
  commentId: string;
  authorId: string;
  viewerId: string;
  reactions: unknown;
  preview: string;
  projectId?: string | null;
  groupCommentKey?: string | null;
  dealId?: string | null;
  audience?: string | null;
  documentId?: string | null;
  pageNumber?: number | null;
  leadId?: string | null;
  createdAt: Date;
}): CommentReactionEvent[] {
  const viewerId = args.viewerId.trim();
  const authorId = args.authorId.trim();
  if (!viewerId || authorId !== viewerId) return [];
  const map = normalizeCommentReactions(args.reactions);
  const out: CommentReactionEvent[] = [];
  for (const [emoji, ids] of Object.entries(map)) {
    for (const actorId of ids) {
      if (!actorId || actorId === viewerId) continue;
      const audience =
        args.audience === 'architect' ? 'architect' : args.audience === 'internal' ? 'internal' : null;
      out.push({
        id: `live:${args.commentId}:${emoji}:${actorId}`,
        commentAuthorId: authorId,
        actorId,
        emoji,
        commentId: args.commentId,
        commentPreview: args.preview,
        projectId: (args.projectId || '').trim() || null,
        groupCommentKey: (args.groupCommentKey || '').trim() || null,
        dealId: (args.dealId || '').trim() || null,
        audience,
        projectLabel: '',
        documentId: (args.documentId || '').trim() || null,
        leadId: (args.leadId || '').trim() || null,
        pdfPageNumber:
          typeof args.pageNumber === 'number' && Number.isFinite(args.pageNumber) ? args.pageNumber : null,
        createdAt: args.createdAt,
      });
    }
  }
  return out;
}

export function reactionEventsFromCommentData(
  commentId: string,
  data: Record<string, unknown>,
  viewerId: string,
): CommentReactionEvent[] {
  const authorId = commentAuthorIdFromData(data);
  const preview = commentPreviewFromFields(
    typeof data.body === 'string' ? data.body : typeof data.text === 'string' ? data.text : typeof data.content === 'string' ? data.content : '',
    typeof data.bodyHtml === 'string' ? data.bodyHtml : '',
  );
  const createdAt = dateFromUnknown(data.updatedAt) > dateFromUnknown(data.createdAt)
    ? dateFromUnknown(data.updatedAt)
    : dateFromUnknown(data.createdAt);
  return eventsFromReactionsMap({
    commentId,
    authorId,
    viewerId,
    reactions: data.reactions,
    preview,
    projectId: typeof data.projectId === 'string' ? data.projectId : null,
    groupCommentKey: typeof data.groupCommentKey === 'string' ? data.groupCommentKey : null,
    dealId: typeof data.dealId === 'string' ? data.dealId : null,
    audience: typeof data.audience === 'string' ? data.audience : null,
    documentId: typeof data.documentId === 'string' ? data.documentId : null,
    pageNumber: typeof data.pageNumber === 'number' ? data.pageNumber : null,
    leadId: typeof data.leadId === 'string' ? data.leadId : null,
    createdAt,
  });
}

export function reactionEventsFromPdfAnnotationData(
  annotationId: string,
  data: Record<string, unknown>,
  viewerId: string,
): CommentReactionEvent[] {
  const createdAt = dateFromUnknown(data.createdAt);
  const projectId = typeof data.projectId === 'string' ? data.projectId : null;
  const documentId = typeof data.documentId === 'string' ? data.documentId : null;
  const pageNumber = typeof data.pageNumber === 'number' ? data.pageNumber : null;
  const audience = typeof data.audience === 'string' ? data.audience : null;
  const root = eventsFromReactionsMap({
    commentId: annotationId,
    authorId: commentAuthorIdFromData(data),
    viewerId,
    reactions: data.reactions,
    preview: commentPreviewFromFields(typeof data.content === 'string' ? data.content : ''),
    projectId,
    documentId,
    pageNumber,
    audience,
    createdAt,
  });
  const replies = Array.isArray(data.replies) ? data.replies : [];
  const replyEvents = replies.flatMap((raw, index) => {
    if (!raw || typeof raw !== 'object') return [];
    const reply = raw as Record<string, unknown>;
    return eventsFromReactionsMap({
      commentId: annotationId,
      authorId: commentAuthorIdFromData(reply),
      viewerId,
      reactions: reply.reactions,
      preview: commentPreviewFromFields(typeof reply.content === 'string' ? reply.content : ''),
      projectId,
      documentId,
      pageNumber,
      audience,
      createdAt: dateFromUnknown(reply.createdAt) || createdAt,
    }).map((event) => ({ ...event, id: `live:${annotationId}:r${index}:${event.emoji}:${event.actorId}` }));
  });
  return [...root, ...replyEvents];
}

export function subscribeLiveAuthorCommentReactions(
  db: Firestore,
  authorId: string,
  onNext: (events: CommentReactionEvent[]) => void,
): Unsubscribe {
  const uid = authorId.trim();
  if (!uid) {
    onNext([]);
    return () => undefined;
  }

  let fromComments: CommentReactionEvent[] = [];
  let fromPdfs: CommentReactionEvent[] = [];
  const emit = () => {
    onNext(sortCommentReactionEventsNewestFirst([...fromComments, ...fromPdfs]).slice(0, COMMENT_REACTION_FEED_LIMIT * 3));
  };

  const commentsCol = collection(db, 'taskComments');
  const orderedComments = query(
    commentsCol,
    where('authorId', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(LIVE_COMMENT_SCAN_LIMIT),
  );
  const unorderedComments = query(commentsCol, where('authorId', '==', uid), limit(LIVE_COMMENT_SCAN_LIMIT));

  let unsubCommentsFallback: Unsubscribe | undefined;
  const unsubComments = onSnapshot(
    orderedComments,
    (snap) => {
      fromComments = snap.docs.flatMap((d) => reactionEventsFromCommentData(d.id, d.data() as Record<string, unknown>, uid));
      emit();
    },
    (error) => {
      console.error('[commentReactionFeed] authored comments listen failed; trying unordered', error);
      unsubCommentsFallback = onSnapshot(
        unorderedComments,
        (snap) => {
          fromComments = snap.docs.flatMap((d) => reactionEventsFromCommentData(d.id, d.data() as Record<string, unknown>, uid));
          emit();
        },
        (fallbackError) => {
          console.error('[commentReactionFeed] authored comments fallback failed', fallbackError);
          fromComments = [];
          emit();
        },
      );
    },
  );

  const unsubPdfs = onSnapshot(
    query(collection(db, 'pdfAnnotations'), where('userId', '==', uid), limit(LIVE_PDF_SCAN_LIMIT)),
    (snap) => {
      fromPdfs = snap.docs.flatMap((d) => reactionEventsFromPdfAnnotationData(d.id, d.data() as Record<string, unknown>, uid));
      emit();
    },
    (error) => {
      console.error('[commentReactionFeed] pdf annotations listen failed', error);
      fromPdfs = [];
      emit();
    },
  );

  return () => {
    unsubComments();
    unsubCommentsFallback?.();
    unsubPdfs();
  };
}
